import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { Select } from '@/shared/components/ui'
import { getDevUser, isDevAuthMode, setDevUser } from '@/shared/config/runtime-config'
import { log } from '@/shared/lib/log'
import { useT } from '@/shared/i18n'

/**
 * Cambiar de usuario sin desloguearse. **Solo en modo dev-auth.**
 *
 * El backend local corre con `AUTH_DEV_MODE=true` y resuelve la identidad con
 * el header `X-Dev-User: <email>`. Eso permite pasar de admin a `tech_member` a
 * contacto de cliente en un clic, que es lo único que hace verificable A OJO el
 * aislamiento entre empresas y entre roles: se entra como cliente, se intenta
 * ir a `/companies`, y se ve qué pasa de verdad. Sin esto haría falta tres
 * cuentas de Firebase y tres navegadores.
 *
 * `isDevAuthMode()` se resuelve en tiempo de build (`import.meta.env.DEV`), así
 * que este componente y la lista de emails desaparecen del bundle de
 * producción. No hay forma de encenderlo desde afuera.
 *
 * Al cambiar de usuario se **vacía toda la cache**: en una app multicompany,
 * dejar datos de la sesión anterior es exactamente la fuga entre usuarios que
 * el resto del sistema se ocupa de evitar.
 */

/**
 * Los que siembra `app/cli.py seed-demo`, uno por rol.
 *
 * El nombre de la persona y el de la empresa **no se traducen** —son datos de
 * la semilla, escritos por gente— pero el rol sí, porque es vocabulario del
 * sistema. De ahí que la etiqueta se arme con `t` en vez de venir escrita.
 */
const SEEDED = [
  { email: 'cesar@leaflatam.com', persona: 'César', roleKey: 'common.role.admin' },
  { email: 'mariana@leaflatam.com', persona: 'Mariana', roleKey: 'common.role.tech_member' },
  { email: 'nicolas@leaflatam.com', persona: 'Nicolás', roleKey: 'common.role.tech_member' },
  { email: 'joaquin@leaflatam.com', persona: 'Joaquín', roleKey: 'common.role.tech_member' },
  { email: 'laura.mendez@nubeandina.com', persona: 'Laura', company: 'Nube Andina' },
  { email: 'ricardo.salas@grupobarrantes.com', persona: 'Ricardo', company: 'Grupo Barrantes' },
  {
    email: 'esteban.quiroga@farmaciasdelvalle.com',
    persona: 'Esteban',
    company: 'Farmacias del Valle',
  },
] as const

/**
 * ¿El cambio salta de un lado del sistema al otro?
 *
 * El lado donde estás se lee de la URL, y el lado a donde vas, de la semilla:
 * en `SEEDED` los clientes son los que tienen `company`. Un email que no esté
 * en la lista —`VITE_DEV_USER` apuntando a otra cosa— se trata como cruce: la
 * raíz siempre es una pantalla válida, y equivocarse para el otro lado deja a
 * alguien mirando un `/403` sin saber por qué.
 */
function cruzaLaFrontera(destino: string): boolean {
  const usuario = SEEDED.find((user) => user.email === destino)
  if (!usuario) return true

  const vasAlPortal = 'company' in usuario
  const estabasEnElPortal = window.location.pathname.startsWith('/portal')
  return vasAlPortal !== estabasEnElPortal
}

export function DevUserSwitcher() {
  const t = useT()
  const client = useQueryClient()
  const [email, setEmail] = useState(() => (isDevAuthMode() ? getDevUser() : ''))

  if (!isDevAuthMode()) return null

  const change = (next: string): void => {
    setEmail(next)
    setDevUser(next)
    log.info('dev_user_changed', { email: next })
    client.clear()
    // Recarga completa: los guards y el layout se eligen con el rol, así que
    // cambiar de usuario en caliente dejaría montado el árbol del rol anterior.
    //
    // **Dónde** se recarga depende de si el cambio cruza la frontera
    // interno/cliente. Quedarse en la misma pantalla es lo que hace legible el
    // aislamiento —`/companies` como Mariana y como Nicolás, lado a lado, son
    // dos listas distintas— pero al cruzar, esa pantalla no existe para quien
    // entra: del portal a un interno caías en "elegí empresa", y de una
    // pantalla interna a un cliente, en `/403`. Ahí manda `RoleHomeRedirect`,
    // que deja a cada rol en su casa.
    if (cruzaLaFrontera(next)) window.location.assign('/')
    else window.location.reload()
  }

  return (
    <div
      className="flex items-center gap-1 rounded-[var(--radius-control)] border border-dashed border-signal/60 px-1.5 py-1 text-2xs text-text-2"
      title={t('dev.tooltip')}
    >
      <span aria-hidden="true">dev</span>
      <Select
        aria-label={t('dev.selectLabel')}
        value={email}
        onChange={(event) => change(event.target.value)}
        className="h-5 max-w-[130px] min-w-0 border-0 bg-transparent px-0 text-2xs shadow-none"
      >
        {SEEDED.some((user) => user.email === email) ? null : (
          <option value={email}>{email}</option>
        )}
        {SEEDED.map((user) => (
          <option key={user.email} value={user.email}>
            {user.persona} ·{' '}
            {'company' in user ? t('dev.clientOf', { empresa: user.company }) : t(user.roleKey)}
          </option>
        ))}
      </Select>
    </div>
  )
}
